import { useEffect, useState } from 'react';
import { useTranslation } from '../i18n/I18nProvider';
import { Button, Card, useToast } from '../ui';
import { IconPhoneDownload, IconShareIos } from './icons';

/**
 * Događaj koji Chromium šalje kad je aplikacija instalabilna. Nije u lib.dom,
 * pa je opisan ovdje umjesto da se `window` gura kroz `any`.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  if (iosStandalone === true) return true;
  // `matchMedia` nema u starijim WebViewima ni u jsdomu — odsutnost nije „instalirano".
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(display-mode: standalone)').matches
    : false;
}

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ se predstavlja kao Macintosh, pa se prepoznaje po dodiru.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

/**
 * Dodavanje portala na početni zaslon.
 *
 * Do izlaska na Trgovinu Play ovo je jedini način da klijent dobije ikonu na
 * mobitelu. Chromium daje `beforeinstallprompt` pa ide pravi gumb; Safari ga
 * nema, pa se tamo prikazuju upute jer drukčije ne ide.
 */
export function InstallApp() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(() => isStandalone());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      // Bez `preventDefault` preglednik prikaže vlastitu traku i događaj propada.
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
      toast(t('install.done'), 'success');
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [t, toast]);

  async function install() {
    if (!deferred) return;
    setBusy(true);
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } finally {
      // Događaj se troši jednom; sljedeći stiže tek na novi posjet.
      setDeferred(null);
      setBusy(false);
    }
  }

  const ios = isIos();

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-accent-light text-accent-deep"
        >
          <IconPhoneDownload width={22} height={22} />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-[20px] font-semibold text-ink">{t('install.title')}</h2>
          <p className="mt-1 text-[14px] leading-relaxed text-ink-muted">{t('install.body')}</p>
        </div>
      </div>

      {installed ? (
        <p className="text-[13.5px] text-ink-muted">{t('install.installed')}</p>
      ) : deferred ? (
        <Button
          className="self-start"
          loading={busy}
          leading={<IconPhoneDownload width={18} height={18} />}
          onClick={() => void install()}
        >
          {t('install.action')}
        </Button>
      ) : (
        <div className="rounded-lg bg-bg-alt p-3">
          <p className="flex items-center gap-2 text-[13.5px] font-medium text-ink">
            {ios && <IconShareIos width={16} height={16} />}
            {ios ? t('install.iosTitle') : t('install.manualTitle')}
          </p>
          <p className="mt-1 text-[13.5px] leading-relaxed text-ink-muted">
            {ios ? t('install.iosSteps') : t('install.manualSteps')}
          </p>
        </div>
      )}
    </Card>
  );
}
